import React, { useState, useCallback } from 'react'

import axios from 'axios'
import { number, shape, string } from 'prop-types'

import I18n from 'Pods/Shared/I18n'

const TRANSLATION_KEY_PREFIX = 'subscription_upgrade'
const DEFAULT_RETURN_PATH = '/profile/settings#tab-settings-subscription'

// Placeholder upgrade checkout page. Renders the Phase 3.1 payload and commits the upgrade
// by POSTing to the commit route (the engine then runs async). The full upgrade UI
// (Solidgate payment section, richer UX) is owned by the FE track (Julio, 86cadefr8) and
// replaces this component.
function SubscriptionUpgrade({
  premiumPrice,
  downloadsUsed,
  unusedCredits,
  discount = 0,
  amountDueNow = 0,
  returnPath = DEFAULT_RETURN_PATH,
  commitUrl = '/profile/subscription/upgrade',
  itemContext = null,
  locale = 'en',
}) {
  const t = (key, options = {}) => I18n.t(key, { ns: 'rails', lng: locale, ...options })
  const [submitting, setSubmitting] = useState(false)

  // The commit route is POST (#upgrade enqueues the worker); a plain link would GET the
  // render action instead, so submit via axios (CSRF header is set globally by the pack).
  const handleUpgrade = useCallback(() => {
    if (submitting) return

    setSubmitting(true)
    axios
      .post(commitUrl)
      .then(() => { window.location.assign(returnPath) })
      .catch(() => { setSubmitting(false) })
  }, [submitting, commitUrl, returnPath])

  return (
    <div className="subscription-upgrade">
      <h1 className="subscription-upgrade__title cgt-heading-300">
        {t(`${TRANSLATION_KEY_PREFIX}.title`)}
      </h1>

      {itemContext && (
        <div className="subscription-upgrade__item">
          {itemContext.thumbnail && (
            <img
              className="subscription-upgrade__item-thumbnail"
              src={itemContext.thumbnail}
              alt={itemContext.modelName}
            />
          )}
          <span className="subscription-upgrade__item-name cgt-body-md">{itemContext.modelName}</span>
        </div>
      )}

      <dl className="subscription-upgrade__summary">
        <div className="subscription-upgrade__row">
          <dt className="subscription-upgrade__label cgt-body-md">
            {t(`${TRANSLATION_KEY_PREFIX}.premium_price`)}
          </dt>
          <dd className="subscription-upgrade__premium-price cgt-body-md-semibold">{premiumPrice}</dd>
        </div>

        <div className="subscription-upgrade__row">
          <dt className="subscription-upgrade__label cgt-body-md">
            {t(`${TRANSLATION_KEY_PREFIX}.downloads_used`)}
          </dt>
          <dd className="subscription-upgrade__downloads-used cgt-body-md">{downloadsUsed}</dd>
        </div>

        <div className="subscription-upgrade__row">
          <dt className="subscription-upgrade__label cgt-body-md">
            {t(`${TRANSLATION_KEY_PREFIX}.unused_credits`)}
          </dt>
          <dd className="subscription-upgrade__unused-credits cgt-body-md">{unusedCredits}</dd>
        </div>

        <div className="subscription-upgrade__row">
          <dt className="subscription-upgrade__label cgt-body-md">
            {t(`${TRANSLATION_KEY_PREFIX}.discount`)}
          </dt>
          <dd className="subscription-upgrade__discount cgt-body-md">{discount}</dd>
        </div>

        <div className="subscription-upgrade__row subscription-upgrade__row--total">
          <dt className="subscription-upgrade__label cgt-body-md-semibold">
            {t(`${TRANSLATION_KEY_PREFIX}.amount_due_now`)}
          </dt>
          <dd className="subscription-upgrade__amount-due cgt-body-md-semibold">{amountDueNow}</dd>
        </div>
      </dl>

      <button
        type="button"
        className="subscription-upgrade__cta cgt-button-text-md-semibold"
        onClick={handleUpgrade}
        disabled={submitting}
      >
        {t(`${TRANSLATION_KEY_PREFIX}.cta`)}
      </button>
    </div>
  )
}

SubscriptionUpgrade.propTypes = {
  premiumPrice: number.isRequired,
  downloadsUsed: number.isRequired,
  unusedCredits: number.isRequired,
  discount: number,
  amountDueNow: number,
  returnPath: string,
  commitUrl: string,
  itemContext: shape({
    modelName: string,
    thumbnail: string,
  }),
  locale: string,
}

export default SubscriptionUpgrade
